import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  Card,
  CardContent,
  MenuItem,
  Stack,
  Chip,
  Link,
  Paper,
  Container,
  Menu,
  alpha,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
  Fab,
  Select,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import {
  Search as SearchIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  Share as ShareIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../config';
import etBusinessLogo from '../assets/etbusiness-logo.svg';
import ScrollToTop from '../components/ScrolltoTop';

interface DirectoryCategory {
  name: string;
  sub_category: string[];
}


interface Directory {
  id: string;
  business_name: string;
  categories: DirectoryCategory[];
  phone_numbers: string[];
  city: string;
  address: string;
  map_url?: string;
  rank?: number;
  analytics?: {
    view_count: number;
    share_count: number;
    contact_count: number;
  };
}

interface Category {
  id: string;
  name: string;
  subCategories: string[];
}

interface FilterContentProps {
  selectedCity: string;
  setSelectedCity: (city: string) => void;
  selectedCategory: string;
  selectedSubCategory: string;
  setSelectedSubCategory: (subCategory: string) => void;
  cities: string[];
  getSubCategories: () => string[];
}

const FilterContent: React.FC<FilterContentProps> = ({
  selectedCity,
  setSelectedCity,
  selectedCategory,
  selectedSubCategory,
  setSelectedSubCategory,
  cities,
  getSubCategories,
}) => {
  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#333', fontWeight: 600 }}>
            City
          </Typography>
          <Select
            fullWidth
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
            displayEmpty
            size="small"
          >
            <MenuItem value="all">All Cities</MenuItem>
            {cities.map((city) => (
              <MenuItem key={city} value={city}>
                {city}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {selectedCategory !== 'all' && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#333', fontWeight: 600 }}>
              Sub Category
            </Typography>
            <Select
              fullWidth
              value={selectedSubCategory}
              onChange={(e) => setSelectedSubCategory(e.target.value)}
              displayEmpty
              size="small"
            >
              <MenuItem value="all">All Sub Categories</MenuItem>
              {getSubCategories().map((subCat) => (
                <MenuItem key={subCat} value={subCat}>
                  {subCat}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default function DirectoryList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [directories, setDirectories] = useState<Directory[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || 'all');
  const [selectedSubCategory, setSelectedSubCategory] = useState('all');
  const [cities, setCities] = useState<string[]>([]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSubCategoryEl, setSelectedSubCategoryEl] = useState<null | HTMLElement>(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const loadingRef = useRef(null);

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedDirectory, setSelectedDirectory] = useState<Directory | null>(null);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);

  const [clientSignature, setClientSignature] = useState<string | null>(null);

  const debouncedSearchTerm = useRef(searchTerm);
  useEffect(() => {
    debouncedSearchTerm.current = searchTerm;
  }, [searchTerm]);

  // Add this near the other refs
  const topContainerRef = useRef<HTMLDivElement>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);

  // Check for directory param first
  useEffect(() => {
    const directoryId = searchParams.get('directory');
    if (directoryId) {
      const fetchSingleDirectory = async () => {
        try {
          setLoading(true);
          const response = await axios.get(`${API_URL}/directories/${directoryId}`);
          if (response.data) {
            setDirectories([response.data]);
            
            // Fetch related directories based on categories
            const relatedCategories = response.data.categories?.map((cat: DirectoryCategory) => cat.name) || [];
            if (relatedCategories.length > 0) {
              const relatedResponse = await axios.get(`${API_URL}/directories`, {
                params: {
                  categories: relatedCategories,
                  limit: 10,
                  exclude: directoryId
                }
              });
              
              if (relatedResponse.data) {
                const relatedDirectories = Array.isArray(relatedResponse.data) 
                  ? relatedResponse.data 
                  : (relatedResponse.data.directories || []);
                
                const filteredRelatedDirectories = relatedDirectories.filter(
                  (dir: Directory) => dir.id !== directoryId
                );
                
                setDirectories(prev => [response.data, ...filteredRelatedDirectories]);
              }
            }

            setHasMore(false); // Disable infinite scroll for single directory view
            if (response.data.city) {
              setCities([response.data.city]);
            }

            setTimeout(() => {
              const element = document.getElementById(`directory-${directoryId}`);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.style.animation = 'highlight 3s ease-in-out';
                element.style.animationIterationCount = "3";
              }
            }, 1000);
          }
        } catch (error) {
          console.error('Error fetching directory:', error);
          setDirectories([]); // Clear directories on error
        } finally {
          setLoading(false);
        }
      };
      fetchSingleDirectory();
    } else {
      // Reset and fetch directories when filters change
      setPage(0);
      setDirectories([]); // Clear existing directories
      setHasMore(true); // Reset hasMore to allow new fetches
      fetchDirectories(true);
    }
  }, [searchParams, selectedCategory, selectedCity, selectedSubCategory]);

  // Separate useEffect for categories - always fetch categories
  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    // Update the useEffect to restore selected city from URL on page load
    const categoryFromURL = searchParams.get('category');
    const cityFromURL = searchParams.get('city');
    if (categoryFromURL) {
      setSelectedCategory(categoryFromURL);
    }
    if (cityFromURL) {
      setSelectedCity(cityFromURL);
    }
  }, [searchParams]);

  const fetchDirectories = async (reset = false) => {
    const directoryId = searchParams.get('directory');
    // Don't fetch directory list if we're showing a single directory
    if (directoryId || loading || (!hasMore && !reset)) return;
    
    try {
      setLoading(true);
      const skip = reset ? 0 : page * 30;
      
      // Build query parameters
      const params: Record<string, string> = {
        skip: skip.toString(),
        limit: '30'
      };
      
      // Add filters to params
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
        if (selectedSubCategory !== 'all') {
          params.subcategory = selectedSubCategory;
        }
      }
      if (selectedCity !== 'all') {
        params.city = selectedCity;
      }
      
      const response = await axios.get(`${API_URL}/directories`, {
        params
      });
      
      // Handle both array response and paginated response
      let newDirectories: Directory[] = [];
      let hasMoreResults = false;

      if (Array.isArray(response.data)) {
        newDirectories = response.data;
        hasMoreResults = newDirectories.length === 30;
      } else {
        newDirectories = response.data.directories || [];
        hasMoreResults = response.data.hasMore || false;
      }
      
      if (reset) {
        setDirectories(newDirectories);
        setPage(1);
      } else {
        setDirectories(prev => [...prev, ...newDirectories]);
        setPage(prev => prev + 1);
      }
      
      setHasMore(hasMoreResults);
    } catch (error) {
      console.error('Error fetching directories:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get<Category[]>(`${API_URL}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const getSubCategories = () => {
    if (selectedCategory === 'all') return [];
    
    // Find the selected category from the categories list
    const selectedCategoryObj = categories.find(cat => cat.name === selectedCategory);
    if (!selectedCategoryObj) return [];
    
    // Return the subcategories from the category object
    return selectedCategoryObj.subCategories.sort();
  };

  const fetchCities = async () => {
    try {
      const response = await axios.get(`${API_URL}/cities`);
      if (response.data && response.data.cities) {
        // Sort cities alphabetically
        const sortedCities = [...response.data.cities].sort((a, b) => a.localeCompare(b));
        setCities(sortedCities);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };


  // Update the filter handling useEffect
  useEffect(() => {
    const directoryId = searchParams.get('directory');
    if (!directoryId) {
      setPage(0);
      setDirectories([]); // Clear existing directories
      setHasMore(true); // Reset hasMore to allow new fetches
      fetchDirectories(true);
      // Scroll to top immediately without animation for filter changes
      window.scrollTo(0, 0);
    }
  }, [searchTerm, selectedCategory, selectedCity, selectedSubCategory, searchParams]);


  // Update the infinite scroll useEffect
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (
          entries[0].isIntersecting && 
          hasMore && 
          !loading && 
          !searchParams.get('directory')
        ) {
          fetchDirectories(false);
        }
      },
      { threshold: 0.1 }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    return () => {
      if (loadingRef.current) {
        observer.unobserve(loadingRef.current);
      }
    };
  }, [hasMore, loading, page, searchParams]);

  // Fetch client signature on mount
  useEffect(() => {
    const storedSignature = localStorage.getItem('clientSignature');
    if (storedSignature) {
      setClientSignature(storedSignature);
    } else {
      fetchClientSignature();
    }
  }, []);

  const fetchClientSignature = async () => {
    try {
      const response = await axios.get(`${API_URL}/analytics/client-signature`);
      const signature = response.data.signature;
      localStorage.setItem('clientSignature', signature);
      setClientSignature(signature);
    } catch (error) {
      console.error('Error fetching client signature:', error);
    }
  };

  const trackAction = async (directoryId: string, actionType: 'view' | 'share' | 'contact') => {
    if (!clientSignature) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/analytics/track/${directoryId}/${actionType}`,
        null,
        {
          params: { client_signature: clientSignature }
        }
      );
      
      // Update local state with new analytics
      setDirectories(prev => 
        prev.map(dir => 
          dir.id === directoryId 
            ? { ...dir, analytics: response.data.analytics }
            : dir
        )
      );
    } catch (error) {
      console.error(`Error tracking ${actionType}:`, error);
    }
  };

  // Track view when directory is loaded from URL
  useEffect(() => {
    const directoryId = searchParams.get('directory');
    if (directoryId && clientSignature) {
      trackAction(directoryId, 'view');
    }
  }, [searchParams, clientSignature]);

  const handleShare = (directory: Directory) => {
    setSelectedDirectory(directory);
    setShareDialogOpen(true);
    setShareLinkCopied(false);
    trackAction(directory.id, 'share');
  };

  const handleCopyLink = () => {
    if (selectedDirectory) {
      const shareUrl = `${window.location.origin}/?directory=${selectedDirectory.id}`;
      navigator.clipboard.writeText(shareUrl);
      setShareLinkCopied(true);
      setTimeout(() => setShareLinkCopied(false), 2000);
    }
  };

  // Add this effect to handle direct access via shared link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const directoryId = params.get('directory');
    
    if (directoryId) {
      const fetchSharedDirectory = async () => {
        try {
          setLoading(true);
          const response = await axios.get(`${API_URL}/directories/${directoryId}`);
          
          if (response.data) {
            // Add the shared directory to the list if it's not already there
            setDirectories(prev => {
              const exists = prev.some(dir => dir.id === response.data.id);
              if (!exists) {
                return [response.data, ...prev];
              }
              return prev;
            });

            // Wait for the directory to be added to the DOM
            setTimeout(() => {
              const element = document.getElementById(`directory-${directoryId}`);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                // Add highlight animation
                element.style.animation = 'none';
                element.offsetHeight; // Trigger reflow
                element.style.animation = 'highlight 2s ease-in-out';
              }
            }, 100);
          }
        } catch (error) {
          console.error('Error fetching shared directory:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchSharedDirectory();
    }
  }, [searchParams]);

  // Add highlight animation styles
  useEffect(() => {
    // Add the highlight animation keyframes to the document
    const style = document.createElement('style');
    style.textContent = `
      @keyframes highlight {
        0% { background-color: rgba(58, 85, 26, 0.1); }
        70% { background-color: rgba(58, 85, 26, 0.1); }
        100% { background-color: transparent; }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handlePhoneClick = (directory: Directory) => {
    trackAction(directory.id, 'contact');
  };

  // Track views for visible directories
  const [viewedDirectories, setViewedDirectories] = useState<Set<string>>(new Set());
  
  const observeDirectoryViews = useCallback((entries: IntersectionObserverEntry[]) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const directoryId = entry.target.id.replace('directory-', '');
        if (!viewedDirectories.has(directoryId)) {
          setViewedDirectories(prev => new Set(prev).add(directoryId));
          trackAction(directoryId, 'view');
        }
      }
    });
  }, [viewedDirectories, trackAction]);

  // Set up intersection observer for directory views
  useEffect(() => {
    const options = {
      threshold: 0.5, // Element is considered viewed when 50% visible
      rootMargin: '0px'
    };

    const observer = new IntersectionObserver(observeDirectoryViews, options);
    
    // Observe all directory cards
    directories.forEach(directory => {
      const element = document.getElementById(`directory-${directory.id}`);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [directories, observeDirectoryViews]);

  // Update the useEffect to fetch cities and categories on mount
  useEffect(() => {
    fetchCategories();
    fetchCities();
  }, []);

  const handleCityMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCityMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCitySelect = (city: string) => {
    window.scrollTo(0, 0);
    setSelectedCity(city);
    handleCityMenuClose();
  };

  const handleSubCategoryMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSelectedSubCategoryEl(event.currentTarget);
  };

  const handleSubCategoryMenuClose = () => {
    setSelectedSubCategoryEl(null);
  };

  const handleSubCategorySelect = (subCategory: string) => {
    window.scrollTo(0, 0);
    setSelectedSubCategory(subCategory);
    handleSubCategoryMenuClose();
  };

  // Add click handlers to scroll to top when filters are changed
  const handleCategoryClick = (categoryName: string) => {
    window.scrollTo(0, 0);
    setSelectedCategory(categoryName);
    setSelectedSubCategory('all');
    setPage(0);
    if (categoryName === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', categoryName);
    }
    setSearchParams(searchParams);
  };

  // Update the search input handler
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    window.scrollTo(0, 0);
    setSearchTerm(e.target.value);
  };

  // Update the scrollToTop function
  const scrollToTop = useCallback(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, []);

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      {/* Add ref to the top Paper component */}
      <Paper 
        elevation={0} 
        sx={{ 
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          bgcolor: 'white',
          backdropFilter: 'blur(8px)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.03)',
        }}
      >
        {/* Enhanced Navigation Bar */}
        <Container maxWidth="lg">
          <Box sx={{ 
            py: 2.5,
            display: 'flex', 
            alignItems: 'center', 
            gap: 3,
          }}>
            <Box
              component="img"
              src={etBusinessLogo}
              alt="ET-Business Logo"
              onClick={() => {
                setSelectedCategory('all');
                setSelectedSubCategory('all');
                setPage(0);
                setDirectories([]);
                setHasMore(true);
                setSearchParams({ category: 'all' });
              }}
              sx={{ 
                height: '40px',
                width: 'auto',
                flexShrink: 0,
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            />
            
            <TextField
              fullWidth
              placeholder="Search businesses, categories, or cities..."
              size="medium"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#f5f5f5',
                  borderRadius: '12px',
                  height: '52px',
                  border: '1px solid #E0E0E0',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    borderColor: '#3A551A',
                  },
                  '& fieldset': {
                    border: 'none',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 20px rgba(58, 85, 26, 0.15)',
                    borderColor: '#3A551A',
                  },
                  '& input': {
                    color: '#333',
                  },
                  '& .MuiInputAdornment-root': {
                    color: '#666',
                  }
                },
              }}
            />
          </Box>
        </Container>
      </Paper>

      {/* Enhanced Categories Bar */}
      <Paper 
        elevation={0} 
        sx={{ 
          bgcolor: 'white',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.03)',
          position: 'sticky',
          top: 84,
          zIndex: 1099,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex',
            gap: 2,
            py: 2,
            overflowX: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}>
            <Button
              variant={selectedCategory === 'all' ? 'contained' : 'text'}
              onClick={() => handleCategoryClick('all')}
              sx={{
                textTransform: 'none',
                borderRadius: '12px',
                px: 2,
                py: 1,
                minWidth: 'fit-content',
                whiteSpace: 'nowrap',
                color: selectedCategory === 'all' ? 'white' : '#666',
                bgcolor: selectedCategory === 'all' ? '#3A551A' : 'transparent',
                boxShadow: selectedCategory === 'all' ? '0 4px 20px rgba(58, 85, 26, 0.3)' : 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: selectedCategory === 'all' ? '#2F4615' : alpha('#000', 0.04),
                  transform: 'translateY(-2px)',
                },
                fontWeight: 600,
              }}
            >
              All
            </Button>
            {categories.map((category) => (
              <Button
                key={category.id}
                variant={selectedCategory === category.name ? 'contained' : 'text'}
                onClick={() => handleCategoryClick(category.name)}
                sx={{
                  textTransform: 'none',
                  borderRadius: '12px',
                  px: 2,
                  py: 1,
                  minWidth: 'fit-content',
                  whiteSpace: 'nowrap',
                  color: selectedCategory === category.name ? 'white' : '#666',
                  bgcolor: selectedCategory === category.name ? '#3A551A' : 'transparent',
                  boxShadow: selectedCategory === category.name ? '0 4px 20px rgba(58, 85, 26, 0.3)' : 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: selectedCategory === category.name ? '#2F4615' : alpha('#000', 0.04),
                    transform: 'translateY(-2px)',
                  },
                  fontWeight: 600,
                }}
              >
                {category.name}
              </Button>
            ))}
          </Box>
        </Container>
      </Paper>

      <Container ref={topContainerRef} maxWidth="lg" sx={{ 
        py: 4,
        px: { xs: 2, sm: 3, md: 4, lg: 6 },
      }}>
        <Box sx={{ display: 'flex', gap: 3, position: 'relative' }}>
          {/* Filtering Card - Desktop */}
          {!isMobile && (
            <Card 
              elevation={0}
              sx={{ 
                width: 280,
                bgcolor: '#fff',
                borderRadius: '16px',
                border: '1px solid',
                borderColor: 'divider',
                position: 'sticky',
                top: 160,
                height: 'fit-content',
                alignSelf: 'flex-start',
                '& .MuiMenu-paper': {
                  width: '280px !important',
                  maxWidth: '280px !important',
                },
              }}
            >
              <CardContent>
                <Box sx={{ p: 1 }}>
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#333', fontWeight: 600 }}>
                        City
                      </Typography>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleCityMenuClick}
                        endIcon={<KeyboardArrowDownIcon />}
                        sx={{
                          justifyContent: 'space-between',
                          textTransform: 'none',
                          color: '#333',
                          borderColor: '#E0E0E0',
                          borderRadius: '12px',
                          py: 1.5,
                          width: '100%',
                        }}
                      >
                        {selectedCity === 'all' ? 'All Cities' : selectedCity}
                      </Button>
                    </Box>

                    {selectedCategory !== 'all' && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#333', fontWeight: 600 }}>
                          Sub Category
                        </Typography>
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={handleSubCategoryMenuClick}
                          endIcon={<KeyboardArrowDownIcon />}
                          sx={{
                            justifyContent: 'space-between',
                            textTransform: 'none',
                            color: '#333',
                            borderColor: '#E0E0E0',
                            borderRadius: '12px',
                            py: 1.5,
                            width: '100%',
                          }}
                        >
                          {selectedSubCategory === 'all' ? 'All Sub Categories' : selectedSubCategory}
                        </Button>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Menus for desktop dropdowns */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCityMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            sx={{
              '& .MuiPaper-root': {
                width: '280px',
                maxHeight: 300,
                mt: 1,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              }
            }}
          >
            <MenuItem
              selected={selectedCity === 'all'}
              onClick={() => handleCitySelect('all')}
            >
              All Cities
            </MenuItem>
            {cities.map((city) => (
              <MenuItem
                key={city}
                selected={city === selectedCity}
                onClick={() => handleCitySelect(city)}
              >
                {city}
              </MenuItem>
            ))}
          </Menu>

          <Menu
            anchorEl={selectedSubCategoryEl}
            open={Boolean(selectedSubCategoryEl)}
            onClose={handleSubCategoryMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            sx={{
              '& .MuiPaper-root': {
                width: '280px',
                maxHeight: 300,
                mt: 1,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              }
            }}
          >
            <MenuItem
              selected={selectedSubCategory === 'all'}
              onClick={() => handleSubCategorySelect('all')}
            >
              All Sub Categories
            </MenuItem>
            {getSubCategories().map((subCat) => (
              <MenuItem
                key={subCat}
                selected={subCat === selectedSubCategory}
                onClick={() => handleSubCategorySelect(subCat)}
              >
                {subCat}
              </MenuItem>
            ))}
          </Menu>

          {/* Main Content Area */}
          <Box ref={mainContentRef} sx={{ flex: 1 }}>
            <Stack spacing={3}>
              {directories.map((directory, index) => {
                const isSharedDirectory = searchParams.get('directory') === directory.id;
                return (
                  <Card
                    key={`${directory.id}-${index}`}
                    id={`directory-${directory.id}`}
                    sx={{ 
                      border: '1px solid',
                      borderColor: isSharedDirectory ? '#3A551A' : '#E0E0E0',
                      borderRadius: '12px',
                      boxShadow: isSharedDirectory ? '0 4px 20px rgba(58, 85, 26, 0.15)' : 'none',
                      position: 'relative',
                      overflow: 'visible',
                      '&:hover': {
                        borderColor: '#3A551A',
                        boxShadow: '0 4px 20px rgba(58, 85, 26, 0.15)',
                      },
                      '& .share-button': {
                        opacity: 0,
                        transition: 'opacity 0.2s',
                      },
                      '&:hover .share-button': {
                        opacity: 1,
                      }
                    }}
                  >
                    <CardContent>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                        gap: 2,
                        flexWrap: 'wrap'
                      }}>
                        <Typography variant="h6" sx={{ mr: 2 }}>
                          {directory.business_name}
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1, 
                          flexWrap: 'wrap',
                          flex: 1,
                          justifyContent: 'flex-end'
                        }}>
                          {directory.categories?.map((cat, idx) => (
                            <Chip 
                              key={`${directory.id}-${cat.name}-${idx}`}
                              label={cat.name}
                              size="medium"
                              sx={{
                                bgcolor: alpha('#3A551A', 0.1),
                                color: '#3A551A',
                                borderRadius: '8px',
                                fontWeight: 600,
                                height: '32px',
                                fontSize: '14px',
                                maxWidth: '100%',
                                transition: 'all 0.2s ease',
                                '& .MuiChip-label': {
                                  whiteSpace: 'normal',
                                  overflow: 'visible',
                                  textOverflow: 'clip',
                                  display: 'block',
                                },
                                '&:hover': {
                                  bgcolor: alpha('#3A551A', 0.15),
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </Box>

                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 2, sm: 6 },
                        color: '#666',
                        mt: 2.5,
                        width: '100%',
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'flex-start', 
                          gap: 1.5,
                          color: '#666',
                          bgcolor: '#F5F7FA',
                          px: { xs: 2, sm: 2.5 },
                          py: { xs: 1.5, sm: 2 },
                          borderRadius: '12px',
                          transition: 'all 0.2s ease',
                          width: { xs: '100%', sm: 'auto' },
                          minWidth: { sm: '280px' },
                          maxWidth: { sm: '400px' },
                          '&:hover': {
                            bgcolor: '#EEF2F6',
                          },
                        }}>
                          <PhoneIcon sx={{ 
                            fontSize: { xs: 20, sm: 24 }, 
                            mt: 0.3,
                            color: '#666',
                            opacity: 0.8
                          }} />
                          <Box 
                            sx={{ 
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: { xs: '8px', sm: '12px' },
                              flex: 1,
                              alignItems: 'center'
                            }}
                          >
                            {Array.isArray(directory.phone_numbers) && directory.phone_numbers.length > 0 ? (
                              directory.phone_numbers.map((phone, idx) => (
                                <Typography 
                                  key={idx}
                                  variant="body1" 
                                  component="a" 
                                  href={`tel:${phone}`}
                                  onClick={() => handlePhoneClick(directory)}
                                  sx={{
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    fontSize: { xs: '0.95rem', sm: '1.1rem' },
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    height: '28px',
                                    bgcolor: 'white',
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: '8px',
                                    border: '1px solid',
                                    borderColor: 'transparent',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      color: 'primary.main',
                                      borderColor: 'primary.main',
                                      transform: 'translateY(-1px)',
                                    },
                                  }}
                                >
                                  {phone}
                                </Typography>
                              ))
                            ) : (
                              <Typography variant="body1">N/A</Typography>
                            )}
                          </Box>
                        </Box>

                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'flex-start', 
                          gap: 1.5,
                          color: '#666',
                          bgcolor: '#F5F7FA',
                          px: { xs: 2, sm: 2.5 },
                          py: { xs: 1.5, sm: 2 },
                          borderRadius: '12px',
                          transition: 'all 0.2s ease',
                          width: { xs: '100%', sm: 'auto' },
                          flex: { sm: 1 },
                          '&:hover': {
                            bgcolor: '#EEF2F6',
                          },
                        }}>
                          <LocationIcon sx={{ fontSize: { xs: 20, sm: 24 }, mt: 0.5 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography 
                              variant="body1"
                              sx={{
                                fontSize: { xs: '0.95rem', sm: '1rem' },
                                fontWeight: 500,
                                wordBreak: 'break-word',
                              }}
                            >
                              {directory.city}, {directory.address}
                            </Typography>
                            {directory.map_url && (
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => window.open(directory.map_url, '_blank')}
                                sx={{
                                  mt: 0.5,
                                  textTransform: 'none',
                                  color: 'primary.main',
                                  p: 0,
                                  fontSize: { xs: '0.9rem', sm: 'inherit' },
                                  '&:hover': {
                                    bgcolor: 'transparent',
                                    textDecoration: 'underline',
                                  },
                                }}
                              >
                                View on Map
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Box>

                      {/* Update analytics display with share button */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2, 
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        color: '#666'
                      }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Typography variant="body2">
                            👁️ {directory.analytics?.view_count || 0} views
                          </Typography>
                          <Typography variant="body2">
                            🔗 {directory.analytics?.share_count || 0} shares
                          </Typography>
                          <Typography variant="body2">
                            📞 {directory.analytics?.contact_count || 0} reaches
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleShare(directory)}
                          sx={{
                            color: '#666',
                            '&:hover': {
                              color: '#3A551A',
                              bgcolor: alpha('#3A551A', 0.1),
                            },
                          }}
                        >
                          <ShareIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
              
              {/* Loading indicator */}
              <Box ref={loadingRef} sx={{ textAlign: 'center', py: 2 }}>
                {loading && (
                  <Typography color="text.secondary">
                    Loading more...
                  </Typography>
                )}
              </Box>
            </Stack>
            
          </Box>
        </Box>
      </Container>

      {/* Filter Drawer - Mobile */}
      {isMobile && (
        <>
          <Drawer
            anchor="right"
            open={filterDrawerOpen}
            onClose={() => setFilterDrawerOpen(false)}
            sx={{
              '& .MuiDrawer-paper': {
                width: '100%',
                maxWidth: '400px',
                height: '100%',
                bgcolor: '#fff',
              },
            }}
          >
            <FilterContent 
              selectedCity={selectedCity}
              setSelectedCity={setSelectedCity}
              selectedCategory={selectedCategory}
              selectedSubCategory={selectedSubCategory}
              setSelectedSubCategory={setSelectedSubCategory}
              cities={cities}
              getSubCategories={getSubCategories}
            />
          </Drawer>

          <Fab
            color="primary"
            aria-label="filter"
            onClick={() => setFilterDrawerOpen(true)}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1000,
              bgcolor: '#3A551A',
              width: 64,
              height: 64,
              '&:hover': {
                bgcolor: '#2F4615',
              },
            }}
          >
            <FilterListIcon sx={{ color: '#fff', fontSize: 32 }} />
          </Fab>
        </>
      )}

      {/* SEO-friendly Footer */}
      <Box 
        component="footer" 
        sx={{ 
          bgcolor: '#F8FAFC',
          borderTop: '1px solid',
          borderColor: 'divider',
          mt: 8,
          py: 6
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* About Section */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ mb: 2, color: '#3A551A', fontWeight: 600 }}>
                About ET Business Directory
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 2, lineHeight: 1.7 }}>
                ET Business Directory is Ethiopia's premier online business listing platform, connecting local businesses with potential customers across the nation. Our comprehensive directory showcases diverse businesses from various sectors, making it easier for people to discover and connect with the services they need.
              </Typography>
              <Stack direction="row" spacing={2}>
                <Link href="/about" sx={{ color: '#3A551A', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                  About Us
                </Link>
                <Link href="/contact" sx={{ color: '#3A551A', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                  Contact
                </Link>
                <Link href="/privacy-policy" sx={{ color: '#3A551A', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                  Privacy Policy
                </Link>
              </Stack>
             
            </Grid>

            {/* Categories Section */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ mb: 2, color: '#3A551A', fontWeight: 600 }}>
                Popular Categories
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 2, lineHeight: 1.7 }}>
                Explore top business categories including restaurants, hotels, retail shops, healthcare providers, educational institutions, and professional services. Our directory features verified listings with detailed information about business locations, contact details, and services offered.
              </Typography>
              <Stack spacing={1}>
                {categories.slice(0, 5).map((category) => (
                  <Link 
                    key={`category-link-${category.id}`}
                    href={`/?category=${category.name}`}
                    sx={{ 
                      color: '#666',
                      textDecoration: 'none',
                      '&:hover': { 
                        color: '#3A551A',
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    {category.name} Businesses
                  </Link>
                ))}
              </Stack>
            </Grid>

            {/* Cities Section */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ mb: 2, color: '#3A551A', fontWeight: 600 }}>
                Featured Cities
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 2, lineHeight: 1.7 }}>
                Find businesses across major Ethiopian cities. Whether you're looking for services in Addis Ababa, Dire Dawa, Bahir Dar, Hawassa, or other cities, our directory helps you locate trusted local businesses in your area. Each listing includes essential information such as addresses, phone numbers, and maps for easy navigation.
              </Typography>
              <Stack spacing={1}>
                {cities.slice(0, 5).map((city) => (
                  <Link 
                    key={`city-link-${city}`}
                    href={`/?city=${city}`}
                    sx={{ 
                      color: '#666',
                      textDecoration: 'none',
                      '&:hover': { 
                        color: '#3A551A',
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Businesses in {city}
                  </Link>
                ))}
              </Stack>
            </Grid>

            {/* SEO Text */}
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: '#666', mt: 4, textAlign: 'justify', lineHeight: 1.7 }}>
                ET Business Directory is your comprehensive guide to Ethiopian businesses, offering an extensive database of local enterprises across various sectors. Our platform facilitates seamless connections between businesses and customers, promoting local economic growth and community development. Whether you're searching for restaurants, hotels, retail shops, healthcare providers, educational institutions, or professional services, our directory provides detailed, up-to-date information to help you make informed decisions. Each listing includes essential details such as business names, categories, contact information, physical addresses, and interactive maps for easy navigation. We're committed to supporting both businesses and consumers by creating a reliable, user-friendly platform that showcases Ethiopia's diverse business landscape. Join our growing community of listed businesses and discover the best services in your area through ET Business Directory.
              </Typography>
            </Grid>

            {/* Copyright */}
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', mt: 4 }}>
                © {new Date().getFullYear()} ET Business Directory. All rights reserved.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Dialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Share Business
          <IconButton
            aria-label="close"
            onClick={() => setShareDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Share link to {selectedDirectory?.business_name}
            </Typography>
            <Paper
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                bgcolor: '#F5F7FA',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedDirectory ? `${window.location.origin}/?directory=${selectedDirectory.id}` : ''}
              </Typography>
              <Button
                startIcon={shareLinkCopied ? <CheckIcon /> : <ContentCopyIcon />}
                onClick={handleCopyLink}
                variant="contained"
                size="small"
                sx={{
                  bgcolor: shareLinkCopied ? '#4CAF50' : '#3A551A',
                  '&:hover': {
                    bgcolor: shareLinkCopied ? '#45a049' : '#2F4615',
                  },
                }}
              >
                {shareLinkCopied ? 'Copied!' : 'Copy'}
              </Button>
            </Paper>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
} 